import {
  CheckCircleOutline as CompletedIcon,
  LocalMallOutlined as ClickAndCollectIcon,
  LockOpenOutlined as DoorUnlockIcon,
  NotificationsOutlined as RequestIcon,
  PointOfSaleOutlined as CheckoutIcon,
  WavingHandOutlined as HelpRequestIcon,
} from "@mui/icons-material";
import { Box, Chip, type ChipProps, Typography } from "@mui/material";
import { alpha, type Theme, useTheme } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { useProductDetails } from "@/pages/store-insights/live-experience/live-view/hooks/useProductDetails";
import { EnrichedRequest } from "@/pages/store-insights/live-experience/live-view/types";
import { Store } from "@/services/features/StoreService";
import { StateRequest } from "@/types/state";
import { formatCurrencyInStore } from "@/utils/currency";

interface RequestCardProps {
  request: StateRequest;
  selectedStore: Store | null;
}

const getRequestStatusColor = (status: StateRequest["status"]): ChipProps["color"] => {
  switch (status) {
    case "COMPLETED":
      return "success";
    case "CREATED":
      return "info";
    case "ASSIGNED":
      return "warning";
    case "MISSED":
    case "ITEM_NOT_FOUND":
      return "error";
    default:
      return "default";
  }
};

const getRequestStatusChipSx = (status: StateRequest["status"], theme: Theme) => {
  switch (status) {
    case "COMPLETED":
      return { bgcolor: "success.lighter" };
    case "CREATED":
      return {
        backgroundColor: alpha(theme.palette.info.main, 0.1),
        borderColor: alpha(theme.palette.info.main, 0.3),
        color: theme.palette.info.dark,
      };
    case "ASSIGNED":
      return { bgcolor: "warning.lighter" };
    case "MISSED":
    case "ITEM_NOT_FOUND":
      return { bgcolor: "error.lighter" };
    default:
      return {
        bgcolor: "grey.100",
        borderColor: "grey.300",
        color: "text.secondary",
      };
  }
};

const humanize = (value: string) =>
  value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const hasItem = (type: StateRequest["type"]) => type === "ITEM" || type === "MOBILE_ITEM";

const RequestCard: FC<RequestCardProps> = ({ request, selectedStore }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const productDetails = useProductDetails(
    hasItem(request.type) ? request.sku : undefined,
    selectedStore?.id,
  );

  const formatPrice = (value?: string | null) =>
    value && selectedStore
      ? formatCurrencyInStore(parseFloat(value.replace(/[^0-9.]/g, "") || "0"), selectedStore)
      : undefined;

  const enrichedItem: EnrichedRequest["enrichedItem"] = productDetails
    ? {
        ...productDetails,
        formattedPrice: formatPrice(productDetails.price),
        formattedSalePrice: formatPrice(productDetails.salePrice),
      }
    : undefined;

  const getRequestStatusLabel = (status: string) => {
    if (status === "ITEM_NOT_FOUND") {
      return t("liveView.fittingRooms.shopperDetails.requests.status.itemNotFound");
    }
    return t(`liveView.fittingRooms.shopperDetails.requests.status.${status}`, humanize(status));
  };
  const getRequestTypeLabel = () => {
    switch (request.type) {
      case "ASSOCIATE":
      case "MOBILE_ASSOCIATE":
        return t("liveView.fittingRooms.shopperDetails.requests.type.help");
      case "CLICK_AND_COLLECT":
        return t("liveView.fittingRooms.shopperDetails.requests.type.clickAndCollect");
      default:
        return humanize(request.type);
    }
  };
  const RequestTypeIcon =
    request.type === "ASSOCIATE" || request.type === "MOBILE_ASSOCIATE"
      ? HelpRequestIcon
      : request.type === "CLICK_AND_COLLECT"
        ? ClickAndCollectIcon
        : request.type === "DOOR_UNLOCK"
          ? DoorUnlockIcon
          : request.type === "CHECKOUT"
            ? CheckoutIcon
            : RequestIcon;
  const requestHasItem = hasItem(request.type);
  const isHelpRequest = request.type === "ASSOCIATE" || request.type === "MOBILE_ASSOCIATE";
  const assignedUserName = request.assignedUser
    ? `${request.assignedUser.firstName} ${request.assignedUser.lastName}`
    : null;
  const isCompletedHelpRequest =
    isHelpRequest && request.status === "COMPLETED" && Boolean(assignedUserName);

  return (
    <Box
      sx={{
        p: 0,
        display: "flex",
        alignItems: "stretch",
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        width: "100%",
        minHeight: 80,
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "stretch", minWidth: 0 }}>
        {requestHasItem && enrichedItem?.imageUrl && (
          <Box
            component="img"
            src={enrichedItem.imageUrl}
            alt={enrichedItem.title || request.sku}
            sx={{
              width: "5.5em",
              minWidth: "5.5em",
              objectFit: "cover",
              display: "block",
              alignSelf: "stretch",
            }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                justifyContent: requestHasItem ? "center" : "flex-start",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, mb: 0.5 }}>
                {!requestHasItem && (
                  <RequestTypeIcon fontSize="small" sx={{ color: "text.primary" }} />
                )}
                <Typography
                  variant="subtitle2"
                  noWrap
                  sx={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: 700,
                  }}
                >
                  {requestHasItem ? enrichedItem?.title || request.sku : getRequestTypeLabel()}
                </Typography>
              </Box>

              {requestHasItem && (
                <Box sx={{ mt: 0.5 }}>
                  {(enrichedItem?.color || enrichedItem?.size) && (
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 0.75 }}>
                      {enrichedItem.size && (
                        <Typography
                          variant="caption"
                          sx={{
                            bgcolor: "action.hover",
                            borderRadius: 1,
                            px: 0.75,
                            py: 0.25,
                            color: "text.secondary",
                          }}
                        >
                          {t("liveView.fittingRooms.shopperDetails.items.sizeValue", {
                            size: enrichedItem.size,
                          })}
                        </Typography>
                      )}
                      {enrichedItem.color && (
                        <Typography
                          variant="caption"
                          sx={{
                            bgcolor: "action.hover",
                            borderRadius: 1,
                            px: 0.75,
                            py: 0.25,
                            color: "text.secondary",
                          }}
                        >
                          {t("liveView.fittingRooms.shopperDetails.items.colorValue", {
                            color: enrichedItem.color,
                          })}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      pt: 2,
                      mb: 0.5,
                    }}
                  >
                    {(enrichedItem?.salePrice || enrichedItem?.price) && (
                      <Typography variant="body2" color="text.primary" fontWeight="medium">
                        {enrichedItem?.formattedSalePrice ||
                          enrichedItem?.formattedPrice ||
                          formatCurrencyInStore(
                            parseFloat(
                              (enrichedItem?.salePrice || enrichedItem?.price || "0").replace(
                                /[^0-9.]/g,
                                "",
                              ),
                            ),
                            selectedStore,
                          )}
                      </Typography>
                    )}
                    {enrichedItem?.salePrice && enrichedItem?.price && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textDecoration: "line-through" }}
                      >
                        {enrichedItem?.formattedPrice ||
                          formatCurrencyInStore(
                            parseFloat((enrichedItem.price || "0").replace(/[^0-9.]/g, "")),
                            selectedStore,
                          )}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {isHelpRequest && assignedUserName && !isCompletedHelpRequest && (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  icon={<CompletedIcon />}
                  label={t("liveView.fittingRooms.shopperDetails.requests.handledBy", {
                    name: assignedUserName,
                  })}
                  sx={{ alignSelf: "flex-start", mt: 0.5, bgcolor: "success.lighter" }}
                />
              )}
              {!isHelpRequest && !requestHasItem && assignedUserName && (
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 0.5, color: "info.main" }}
                >
                  {t("liveView.fittingRooms.shopperDetails.requests.assignedToValue", {
                    name: assignedUserName,
                  })}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                justifyContent: "flex-start",
                ml: 2,
                p: 1.5,
              }}
            >
              {isCompletedHelpRequest ? (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  icon={<CompletedIcon />}
                  label={t("liveView.fittingRooms.shopperDetails.requests.completedBy", {
                    name: assignedUserName,
                  })}
                  sx={{ bgcolor: "success.lighter", fontWeight: 600 }}
                />
              ) : (
                <Chip
                  size="small"
                  color={getRequestStatusColor(request.status)}
                  variant="outlined"
                  label={getRequestStatusLabel(request.status)}
                  sx={{ fontWeight: 600, ...getRequestStatusChipSx(request.status, theme) }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {requestHasItem && assignedUserName && (
        <Box
          sx={{
            mx: 1.5,
            mt: 1.5,
            mb: 1.5,
            px: 1.5,
            py: 1,
            bgcolor: "grey.100",
            borderRadius: (currentTheme) => {
              const parentRadius = Number.parseFloat(String(currentTheme.shape.borderRadius)) * 2;
              return `max(0px, calc(${parentRadius}px - ${currentTheme.spacing(1.5)}))`;
            },
          }}
        >
          <Typography variant="body2" sx={{ color: "common.black" }}>
            {t("liveView.fittingRooms.shopperDetails.requests.assignedToValue", {
              name: assignedUserName,
            })}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default RequestCard;
